import { makeid } from 'utils/helpers/makeid';

import {
  nurseContactWithPatientItem,
  nurseDecisionMakingNextStepsItem,
  nurseDigitalEngagementEducationMethodItem,
  nurseEmergencyCareAdvisedItem,
  nurseMaTasksDelegatedItem,
  nurseNoCallMadeRationaleItem,
  nursePatientAcknowledgementItem,
  nursePatientAgreeabilityItem,
  nurseReviewEscalationProtocolItem,
} from '../common';

import type { TFormItem } from '../../types';

export const spo2ClinicalAssessmentList = [
  { title: 'Shortness of breath at rest', name: 'shortnessOfBreathAtRest' },
  { title: 'Chest pain/pressure', name: 'chestPainPressure' },
  {
    title: 'Confusion/altered mental status',
    name: 'confusionAlteredMentalStatus',
  },
  {
    title: 'Blue lips/nails (cyanosis)',
    name: 'blueLipsNailsCyanosis',
  },
  { title: 'Severe weakness/fatigue', name: 'severeWeaknessFatigue' },
  {
    title: 'Unable to speak/excessive breathlessness',
    name: 'unableToSpeakExcessiveBreathlessness',
  },
  { title: 'Cough/fever', name: 'coughFever' },
  {
    title: 'Shortness of breath with mild activity',
    name: 'shortnessOfBreath',
  },
  { title: 'Mild fatigue', name: 'mildFatigue' },
  { title: 'Sleep disturbances', name: 'sleepDisturbances' },
  { title: 'Use of oxygen as prescribed', name: 'useOfOxygenAsPrescribed' },
  {
    title: 'Recent illness/hospitalization',
    name: 'recentIllnessHospitalization',
  },
];

const yesNoClinicalAssessment = [
  'useOfOxygenAsPrescribed',
  'recentIllnessHospitalization',
];

const clinicalAssessmentItems: TFormItem[] = spo2ClinicalAssessmentList.map(
  (item) => {
    const presentDeniedOptions = [
      { label: 'Present', value: true, key: makeid(10) },
      { label: 'Denied', value: false, key: makeid(10) },
    ];
    const yesNoOptions = [
      { label: 'Yes (describe)', value: true, key: makeid(10) },
      { label: 'No', value: false, key: makeid(10) },
    ];
    return {
      key: makeid(10),
      title: <p>{item.title}:</p>,
      type: 'radio',
      name: item.name,
      required: false,
      options: yesNoClinicalAssessment.includes(item.name)
        ? yesNoOptions
        : presentDeniedOptions,
    };
  },
);

export const spo2: TFormItem[] = [
  {
    key: makeid(10),
    title: <p>Chart Review: At-risk trend summary:</p>,
    type: 'empty',
    name: '_empty',
    items: [
      {
        key: makeid(10),
        title: (
          <p>
            Highest SpO2: <span>__ %</span>
          </p>
        ),
        type: 'input-number',
        required: false,
        name: 'highestSpo2',
        validation: { min: 0 },
      },
      {
        key: makeid(10),
        title: (
          <p>
            Lowest SpO2: <span>__ %</span>
          </p>
        ),
        type: 'input-number',
        required: false,
        name: 'lowestSpo2',
        validation: { min: 0 },
      },
      {
        key: makeid(10),
        title: (
          <p>
            Highest pulse: <span>__ bpm</span>
          </p>
        ),
        type: 'input-number',
        required: false,
        name: 'highestPulse',
        validation: { min: 0 },
      },
      {
        key: makeid(10),
        title: (
          <p>
            Lowest pulse: <span>__ bpm</span>
          </p>
        ),
        type: 'input-number',
        required: false,
        name: 'lowestPulse',
        validation: { min: 0 },
      },
      {
        key: makeid(10),
        title: <p>Readings are trending:</p>,
        type: 'radio',
        required: false,
        name: 'typeofTrend',
        options: [
          {
            label: 'High',
            value: 'high',
            key: makeid(10),
          },
          {
            label: 'Low',
            value: 'low',
            key: makeid(10),
          },
          {
            label: 'Both',
            value: 'high&low',
            key: makeid(10),
          },
          {
            label: 'Fluctuating',
            value: 'fluctuating',
            key: makeid(10),
          },
        ],
      },
      {
        key: makeid(10),
        title: null,
        name: 'typeofTrendComment',
        type: 'textarea',
      },
    ],
  },
  { ...nurseContactWithPatientItem },
  { ...nurseNoCallMadeRationaleItem },
  {
    key: makeid(10),
    title: <p>Clinical Assessment</p>,
    type: 'empty',
    name: '_empty',
    items: [
      ...clinicalAssessmentItems,
      {
        key: makeid(10),
        title: <p>Other acute concerns:</p>,
        type: 'textarea',
        name: 'clinicalAssessmentComment',
      },
    ],
  },
  { ...nurseReviewEscalationProtocolItem },
  { ...nurseEmergencyCareAdvisedItem },
  {
    key: makeid(10),
    title: <p>Potential contributing factors:</p>,
    type: 'checkbox',
    required: false,
    name: 'potentialContributingFactors',
    options: [
      {
        label: 'Non-adherence to O2',
        value: 'non-adherence to O2',
        key: makeid(10),
      },
      {
        label: 'Device/measurement error',
        value: 'device/measurement error',
        key: makeid(10),
      },
      {
        label: 'Illness/infection',
        value: 'illness/infection',
        key: makeid(10),
      },
      {
        label: 'Activity/positioning',
        value: 'activity/positioning',
        key: makeid(10),
      },
      {
        label: 'No identifiable cause',
        value: 'no identifiable cause',
        key: makeid(10),
      },
    ],
    items: [
      {
        key: makeid(10),
        title: null,
        type: 'textarea',
        name: 'potentialContributingFactorsComment',
      },
    ],
  },
  { ...nurseDecisionMakingNextStepsItem },
  {
    key: makeid(10),
    type: 'checkbox',
    title: <p>Patient education provided:</p>,
    name: 'patientEducationProvided',
    required: false,
    options: [
      {
        label: 'Oxygen safety/use',
        value: 'oxygen safety/use',
        key: makeid(10),
      },
      {
        label: 'Device technique',
        value: 'device technique',
        key: makeid(10),
      },
      {
        label: 'Warning signs',
        value: 'warning signs',
        key: makeid(10),
      },
      {
        label: 'Activity management',
        value: 'activity management',
        key: makeid(10),
      },
      {
        label: 'Smoking cessation',
        value: 'smoking cessation',
        key: makeid(10),
      },
    ],
    items: [
      {
        key: makeid(10),
        title: null,
        type: 'textarea',
        name: 'patientEducationProvidedComment',
      },
    ],
  },
  { ...nurseDigitalEngagementEducationMethodItem },
  { ...nurseMaTasksDelegatedItem },
  { ...nursePatientAcknowledgementItem },
  { ...nursePatientAgreeabilityItem },
];
